import { z } from 'zod';
import {
  CalendarEventId,
  HouseholdId,
  IsoDatetime,
  LangCode,
  OperationId,
  OperationProofId,
  OperationUpdateId,
  RoutineId,
  UserId,
} from './primitives.js';

export const OperationState = z.enum([
  'draft',
  'scheduled',
  'sent',
  'seen',
  'in_progress',
  'waiting_proof',
  'waiting_approval',
  'approved',
  'rejected',
  'overdue',
  'escalated',
  'cancelled',
]);
export type OperationState = z.infer<typeof OperationState>;

export const OperationPriority = z.enum(['normal', 'important', 'emergency']);
export type OperationPriority = z.infer<typeof OperationPriority>;

export const ProofType = z.enum(['photo', 'voice', 'text']);
export type ProofType = z.infer<typeof ProofType>;

export const ChecklistItem = z.object({
  id: z.string(),
  label: z.string().min(1).max(200),
  done: z.boolean().default(false),
});
export type ChecklistItem = z.infer<typeof ChecklistItem>;

export const OperationOrigin = z.enum(['manual', 'routine', 'suggestion', 'quick_command']);

export const Operation = z.object({
  id: OperationId,
  household_id: HouseholdId,
  title: z.string().min(1).max(200),
  description: z.string().max(2000).nullable(),
  assignee_user_id: UserId.nullable(),
  created_by_user_id: UserId,
  state: OperationState,
  priority: OperationPriority,
  scheduled_for: IsoDatetime.nullable(),
  due_at: IsoDatetime.nullable(),
  location_text: z.string().max(200).nullable(),
  proof_required: z.boolean(),
  proof_types: z.array(ProofType),
  checklist: z.array(ChecklistItem),
  origin: OperationOrigin,
  origin_routine_id: RoutineId.nullable(),
  source_lang: LangCode,
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
});
export type Operation = z.infer<typeof Operation>;

export const CreateOperationRequest = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  assignee_user_id: UserId.optional(),
  priority: OperationPriority.default('normal'),
  scheduled_for: IsoDatetime.optional(),
  due_at: IsoDatetime.optional(),
  location_text: z.string().max(200).optional(),
  proof_required: z.boolean().default(false),
  proof_types: z.array(ProofType).default([]),
  checklist: z.array(z.object({ label: z.string().min(1).max(200) })).default([]),
  reference_r2_keys: z.array(z.string()).max(10).default([]),
  // The event this task is part of, when it was created from one. Optional
  // everywhere: most tasks are just tasks.
  calendar_event_id: CalendarEventId.optional(),
  // One per user-initiated create, reused verbatim across every retry of
  // that same write (offline-queue replay, timeout-after-success, double
  // tap) — migration 0049. Optional so any caller that predates this stays
  // valid; a create without one is simply not deduplicated.
  idempotency_key: z.string().max(64).optional(),
});
export type CreateOperationRequest = z.infer<typeof CreateOperationRequest>;

export const TransitionTarget = z.enum([
  'sent',
  'seen',
  'in_progress',
  'waiting_proof',
  'waiting_approval',
  'approved',
  'rejected',
  'cancelled',
]);
export type TransitionTarget = z.infer<typeof TransitionTarget>;

/**
 * Why a piece of work was rejected, as a structured code beside the free-text
 * reason (migration 0051). The taxonomy is deliberately about the WORK and
 * the INSTRUCTION, not the person — 'unclear_instruction' blames the brief,
 * which is something the household can fix.
 */
export const RejectionReasonCode = z.enum([
  'unclear_instruction',
  'missing_preference',
  'new_domain',
  'missing_access',
  'delivery_failure',
  'language',
  'timing',
  'execution',
  'other',
]);
export type RejectionReasonCode = z.infer<typeof RejectionReasonCode>;

export const TransitionRequest = z.object({
  reason: z.string().max(500).optional(),
  proof_id: OperationProofId.optional(),
  // Optional, additive, and only persisted on the reject path — the
  // structured half of `reason` (see RejectionReasonCode above).
  reason_code: RejectionReasonCode.optional(),
});

export const UpdateKind = z.enum([
  'instruction',
  'reminder',
  'question',
  'answer',
  'note',
  'approval',
  'rejection',
  'issue',
]);
export type UpdateKind = z.infer<typeof UpdateKind>;

export const OperationUpdate = z.object({
  id: OperationUpdateId,
  household_id: HouseholdId,
  operation_id: OperationId,
  author_user_id: UserId,
  kind: UpdateKind,
  body: z.string().min(1).max(2000),
  source_lang: LangCode,
  voice_r2_key: z.string().nullable(),
  created_at: IsoDatetime,
});

export const CreateUpdateRequest = z.object({
  kind: UpdateKind,
  body: z.string().min(1).max(2000),
  source_lang: LangCode,
  voice_r2_key: z.string().optional(),
  // One per user-initiated submit — see migration 0038. Optional so any
  // caller that predates this stays valid; a submit without one is simply
  // not deduplicated.
  idempotency_key: z.string().max(64).optional(),
});

export const OperationProof = z.object({
  id: OperationProofId,
  household_id: HouseholdId,
  operation_id: OperationId,
  uploaded_by_user_id: UserId,
  type: ProofType,
  r2_key: z.string().nullable(),
  text_body: z.string().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  bytes: z.number().int().nullable(),
  sha256: z.string().nullable(),
  thumbnail_r2_key: z.string().nullable(),
  created_at: IsoDatetime,
});

export const RegisterProofRequest = z.object({
  type: ProofType,
  r2_key: z.string().optional(),
  text_body: z.string().max(2000).optional(),
  // One per user-initiated submit, reused across every retry of that same
  // finalize — the upload to R2 succeeded but the response never arrived
  // (migration 0049). Optional; a proof without one is not deduplicated.
  idempotency_key: z.string().max(64).optional(),
});

export const QuickCommandRequest = z.object({
  text: z.string().max(2000).optional(),
  voice_url: z.string().optional(),
  attachments: z
    .array(
      z.object({
        kind: z.enum(['image', 'pdf']),
        r2_key: z.string(),
      }),
    )
    .optional(),
  lang_hint: LangCode.optional(),
});

export const ParsedOperationDraft = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  assignee_user_id: z.string().optional(),
  scheduled_for: IsoDatetime.optional(),
  due_at: IsoDatetime.optional(),
  priority: z.enum(['normal', 'important']),
  proof_required: z.boolean(),
  proof_types: z.array(ProofType),
  checklist: z
    .array(z.object({ label: z.string().min(1).max(200) }))
    .max(20)
    .optional(),
  memory_used: z.array(z.string()),
  language_detected: LangCode,
  confidence: z.number().min(0).max(1),
});
export type ParsedOperationDraft = z.infer<typeof ParsedOperationDraft>;

export const ParsedMultiOpResponse = z.object({
  operations: z.array(ParsedOperationDraft).max(5),
  meta: z.object({ split_reason: z.string() }),
});
